import { getUserId, getAccessToken } from './liff';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/** Identity headers: the access token is what the API trusts; the user id is a hint for local dev. */
function lineHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  const token = getAccessToken();
  const userId = getUserId();
  if (token) h['X-Line-Access-Token'] = token;
  if (userId) h['X-Line-User-Id'] = userId;
  return h;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...lineHeaders(),
    ...(options.headers as Record<string, string> | undefined),
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `HTTP ${response.status}`,
        message: data.message,
      };
    }

    return data;
  } catch (error) {
    console.error('API request failed:', error);
    return {
      success: false,
      error: 'Network error',
      message: 'Unable to connect to server',
    };
  }
}

// LIFF API endpoints
export const api = {
  // Portal - Homepage
  // SECURITY: Include X-Line-User-Id header for user data verification
  getPortalHome: (lineUserId?: string) =>
    request<any>(`/portal/home${lineUserId ? `?lineUserId=${lineUserId}` : ''}`, lineUserId ? {
      headers: {
        'X-Line-User-Id': lineUserId,
      },
    } : undefined),

  // Portal - List events with filters
  getPortalEvents: (params?: {
    category?: string;
    search?: string;
    sort?: string;
    page?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sort) searchParams.set('sort', params.sort);
    if (params?.page) searchParams.set('page', params.page);
    const query = searchParams.toString();
    return request<any>(`/portal/events${query ? `?${query}` : ''}`);
  },

  // Portal - Get categories
  getPortalCategories: () =>
    request<any[]>('/portal/categories'),

  // Get event for display
  getEvent: (eventId: string, preview?: boolean) =>
    request<any>(`/liff/events/${eventId}${preview ? '?preview=true' : ''}`),

  // Submit booking
  submitBooking: (data: {
    eventId: string;
    lineUserId?: string;
    lineDisplayName?: string;
    linePictureUrl?: string;
    formData: Record<string, any>;
    selectedCategoryId?: string;
    selectedShippingId?: string;
    merchandiseItems?: { itemId: string; quantity: number }[];
    participants?: Record<string, any>[];
  }) =>
    request<{ id: string; totalAmount: number }>(`/liff/events/${data.eventId}/submit`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Get booking status
  // SECURITY: backend requires BOTH query param AND matching X-Line-User-Id header (IDOR protection)
  getBooking: (bookingId: string, lineUserId?: string) =>
    request<any>(
      `/liff/bookings/${bookingId}${lineUserId ? `?lineUserId=${lineUserId}` : ''}`,
      lineUserId ? { headers: { 'X-Line-User-Id': lineUserId } } : undefined,
    ),

  // Get user's bookings
  // SECURITY: Include X-Line-User-Id header for verification
  getUserBookings: (lineUserId: string) =>
    request<any[]>(`/liff/user/${lineUserId}/bookings`, {
      headers: {
        'X-Line-User-Id': lineUserId,
      },
    }),

  // Upload file (public LIFF endpoint — requires lineUserId + published eventId)
  uploadFile: async (file: File, eventId: string, fieldId?: string): Promise<ApiResponse<{ id: string; url: string; originalName: string; fileType: string; fileSize: number }>> => {
    const lineUserId = getUserId();
    if (!lineUserId) {
      return { success: false, error: 'LINE user not initialized' };
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventId', eventId);
    if (fieldId) formData.append('fieldId', fieldId);

    try {
      const response = await fetch(`${API_BASE}/liff/upload`, {
        method: 'POST',
        body: formData,
        headers: lineHeaders(),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || `HTTP ${response.status}` };
      }
      return data;
    } catch (err) {
      console.error('Upload failed:', err);
      return { success: false, error: 'Network error' };
    }
  },
};

export default api;
